import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const SimulationStep2 = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    civility: '',
    lastName: '',
    firstName: '',
    birthDate: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    // Check if step 1 was completed
    const step1Data = sessionStorage.getItem('simulation_step1');
    if (!step1Data) {
      navigate('/simulation/step1');
    }
  }, [navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContinue = () => {
    // Validate required fields
    if (!formData.civility || !formData.lastName || !formData.firstName || 
        !formData.birthDate || !formData.email || !formData.phone) {
      alert('Veuillez remplir tous les champs');
      return;
    }
    
    // Store in sessionStorage for next step
    sessionStorage.setItem('simulation_step2', JSON.stringify(formData));
    navigate('/simulation/step3');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_younited-clone-1/artifacts/y6hpz6h2_IMG_0359.jpeg"
            alt="Younited"
            className="h-10 object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <Button 
            variant="outline" 
            className="text-white border-white hover:bg-white hover:text-black rounded-full px-6"
            onClick={() => navigate('/login')}
          >
            Espace Client
          </Button>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center space-x-8">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">✓</div>
              <span className="text-green-600 font-medium">Votre projet</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-sm">2</div>
              <span className="text-purple-600 font-medium">Vos coordonnées</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-500 flex items-center justify-center font-bold text-sm">3</div>
              <span className="text-gray-400">Récapitulatif</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="p-8 shadow-lg">
          <h1 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Vos coordonnées
          </h1>

          {/* Civility */}
          <div className="mb-6">
            <Label className="block text-sm font-semibold text-gray-700 mb-3">
              Civilité
            </Label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => handleChange('civility', 'M')}
                className={`flex-1 py-4 px-6 rounded-xl border-2 font-medium transition-all ${
                  formData.civility === 'M'
                    ? 'border-purple-600 bg-purple-50 text-purple-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                Monsieur
              </button>
              <button
                type="button"
                onClick={() => handleChange('civility', 'Mme')}
                className={`flex-1 py-4 px-6 rounded-xl border-2 font-medium transition-all ${
                  formData.civility === 'Mme'
                    ? 'border-purple-600 bg-purple-50 text-purple-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                Madame
              </button>
            </div>
          </div>

          {/* Name Fields */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label className="block text-sm font-semibold text-gray-700 mb-2">
                Nom
              </Label>
              <Input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder="Votre nom"
                className="h-14 rounded-xl border-2 border-gray-200 focus:border-purple-500"
              />
            </div>
            <div>
              <Label className="block text-sm font-semibold text-gray-700 mb-2">
                Prénom
              </Label>
              <Input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="Votre prénom"
                className="h-14 rounded-xl border-2 border-gray-200 focus:border-purple-500"
              />
            </div>
          </div>

          {/* Birth Date */}
          <div className="mb-6">
            <Label className="block text-sm font-semibold text-gray-700 mb-2">
              Date de naissance
            </Label>
            <Input
              type="date"
              value={formData.birthDate}
              onChange={(e) => handleChange('birthDate', e.target.value)}
              className="h-14 rounded-xl border-2 border-gray-200 focus:border-purple-500"
            />
          </div>

          {/* Email */}
          <div className="mb-6">
            <Label className="block text-sm font-semibold text-gray-700 mb-2">
              Email
            </Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="votre@email.com"
              className="h-14 rounded-xl border-2 border-gray-200 focus:border-purple-500"
            />
          </div>

          {/* Phone */}
          <div className="mb-8">
            <Label className="block text-sm font-semibold text-gray-700 mb-2">
              Téléphone
            </Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="06 XX XX XX XX"
              className="h-14 rounded-xl border-2 border-gray-200 focus:border-purple-500"
            />
          </div>

          {/* Buttons */}
          <div className="flex space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/simulation/step1')}
              className="flex-1 h-14 font-bold text-lg rounded-xl"
            >
              Retour
            </Button>
            <Button
              onClick={handleContinue}
              className="flex-1 h-14 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              style={{ backgroundColor: '#8B5CF6' }}
            >
              Continuer
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SimulationStep2;
